import './src/styles/global.css';
import React from 'react';
import DefaultLayout from './src/layouts/DefaultLayout';
import LegacyLayout from './src/layouts/LegacyLayout';
import Analytics from './src/components/Analytics';
import { useGlobalUserIdentification } from './src/hooks/useGlobalUserIdentification';
import { trackPageView } from './src/utils/metaTracking';
import { isPreviewOrInternalDomain } from './src/utils/environmentUtils';


// Global User Identification Component
const GlobalUserIdentification = () => {
  useGlobalUserIdentification();
  return null; // This component doesn't render anything
};

// Performance optimizations
export const onClientEntry = () => {
  // Prefetch critical resources on idle
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      // Preload critical images and resources
      const criticalResources = [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
      ];

      criticalResources.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = url;
        document.head.appendChild(link);
      });
    });
  }


};

// Root wrapper to add global identification to all pages
export const wrapRootElement = ({ element }) => {
  return (
    <>
      <GlobalUserIdentification />
      {element}
    </>
  );
};

// Wrap all pages with the appropriate layout component
export const wrapPageElement = ({ element, props }) => {
  // Check if the page has opted out of the layout
  const pathname = props.location.pathname;
  const cleanPath = pathname.replace(/\/+$/, '') || '/'; // Remove trailing slash, but keep '/' for root
  
  // Pages that need the legacy layout (old Optima font styling)
  const legacyLayoutPaths = [
    '/404',
    '/about',
    '/apply',
    '/bring-us-to-your-city',
    '/contact',
    '/experience',
    '/host-a-show',
    '/partner',
    '/performer-resources',
    '/privacy',
    '/private-events',
    '/private',
    '/songs',
    '/terms',
    '/why-perform',
  ];

  if (legacyLayoutPaths.includes(cleanPath)) {
    // Use LegacyLayout for pages that need the old styling
    return (
      <LegacyLayout {...props}>
        <Analytics />
        {element}
      </LegacyLayout>
    );
  }

  // Use DefaultLayout for all other pages (new modern styling)
  return (
    <DefaultLayout {...props}>
      <Analytics />
      {element}
    </DefaultLayout>
  );
};

// Enhanced PageView tracking with improved timing for FBP capture
export const onInitialClientRender = () => {
  // Only track in production environments using unified environment check
  if (!isPreviewOrInternalDomain()) {
    // Strategy 1: Immediate tracking for users with existing _fbp cookie
    const hasExistingFbp = document.cookie.includes('_fbp=');

    if (hasExistingFbp) {
      // Track immediately if _fbp already exists (repeat visitor)
      trackPageView({
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        initial_load: true,
        fbp_strategy: 'immediate_existing_fbp',
      });
    } else {
      // Strategy 2: Delayed tracking for new visitors to allow Meta Pixel to set _fbp
      // This significantly improves FBP capture rate for first-time visitors

      // Single delayed tracking for new visitors (800ms optimal for FBP capture)
      setTimeout(() => {
        trackPageView({
          url: window.location.href,
          title: document.title,
          path: window.location.pathname,
          initial_load: true,
          fbp_strategy: 'delayed_800ms_new_visitor',
        });
      }, 800);
    }
  }
};

// Enhanced route update tracking with FBP optimization
export const onRouteUpdate = ({ location, prevLocation }) => {
  // Only track in production environments
  if (!isPreviewOrInternalDomain()) {
    // Small delay to ensure any route-specific changes are complete
    // and to give Meta Pixel time to update if needed
    setTimeout(() => {
      trackPageView({
        url: window.location.href,
        title: document.title,
        path: location.pathname,
        route_update: true,
        previous_path: prevLocation?.pathname,
      });
    }, 300); // Shorter delay since pixel is likely already loaded
  }
};

// Handle page transitions - fix Gatsby v5 scroll restoration bug
export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  // Handle hash links properly
  if (location.hash) {
    window.history.scrollRestoration = 'auto';
    return true;
  }

  // Manual scroll restoration for Gatsby v5 bug
  window.history.scrollRestoration = 'manual';
  
  const currentPosition = getSavedScrollPosition(location, location.key);
  const top = currentPosition ? currentPosition[1] : 0;

  // Use setTimeout to move scroll to later in event loop (prevents interruption)
  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      try {
        window.scrollTo({ top, behavior: 'instant' });
      } catch (e) {
        // Fallback for older browsers
        window.scrollTo(0, top);
      }
    });
  }, 0);

  return false;
};

// Enable Gatsby's default scroll restoration
export const disableCorePrefetching = () => false;
